import { RootState } from 'store';
import { WizardConfig } from './types';
import { memo } from 'react';
import { WizardProvider } from './WizardProvider';
import { Wizard } from './Wizard';
import { useSelector } from 'react-redux';
import { Flex } from '@chakra-ui/react';

interface WizardContainerProps {
    config: WizardConfig;
    initialData?: Record<string, any>;
    metadata?: Record<string, any>;
    readOnlyData?: Record<string, any>;
    onAfterSubmit?: () => void;
    onCancel?: () => void;
}

/**
 * WizardContainer is the top-level component that orchestrates the complete wizard system.
 *
 * This component serves as the entry point for all wizard functionality, handling:
 * - Wizard initialization and Redux state management
 * - Configuration processing and validation
 * - Loading and error states during wizard startup
 * - Provider setup for wizard context and data flow
 *
 * Architecture:
 * - Dispatches startWizard action to initialize Redux state with config and data
 * - Wraps the wizard in WizardProvider for context-based data access
 * - Renders the main Wizard component within a full-height container
 * - Handles wizard lifecycle from initialization through completion
 *
 * State Management:
 * - Connects to Redux store for global wizard state
 * - Monitors loading and error states during initialization
 * - Passes through initial data, metadata, and readonly data to child components
 *
 * @param config - Wizard configuration defining steps, validation, and behavior
 * @param initialData - Optional pre-populated form data for wizard fields
 * @param metadata - Optional additional data accessible throughout wizard lifecycle
 * @param onAfterSubmit - Optional callback executed after successful wizard submission
 */
function WizardContainer({ config, onAfterSubmit, onCancel }: WizardContainerProps) {
    const { isLoading, error } = useSelector((state: RootState) => state.wizard);

    if (isLoading) {
        return <div>Loading Wizard...</div>;
    }
    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <Flex h="full" pos={'relative'}>
            <WizardProvider config={config} onAfterSubmit={onAfterSubmit} onCancel={onCancel}>
                <Wizard />
            </WizardProvider>
        </Flex>
    );
}

export default memo(WizardContainer);
